// Numerology Engine v5 — Chaldean name numbers, corrected zodiac cusps, dynamic Personal Year.
// All computations deterministic. No apostrophes in user-facing strings.

// Chaldean letter values (Vedic-aligned; NOT Pythagorean). 9 is sacred/unused.
const CHALDEAN = {
  a: 1, b: 2, c: 3, d: 4, e: 5, f: 8, g: 3, h: 5, i: 1,
  j: 1, k: 2, l: 3, m: 4, n: 5, o: 7, p: 8, q: 1, r: 2,
  s: 3, t: 4, u: 6, v: 6, w: 6, x: 5, y: 1, z: 7,
};

const MASTER_NUMBERS = [11, 22, 33];

function sumDigits(n) {
  return String(n)
    .split("")
    .filter((c) => /\d/.test(c))
    .reduce((acc, d) => acc + parseInt(d, 10), 0);
}

function reduceToSingle(n, allowMaster = true) {
  let num = Math.abs(parseInt(n, 10));
  while (num > 9) {
    if (allowMaster && MASTER_NUMBERS.includes(num)) return num;
    num = sumDigits(num);
  }
  return num;
}

// DOB expected: YYYY-MM-DD
export function calcLifePath(dob) {
  if (!dob) return null;
  const [y, m, d] = dob.split("-").map((x) => parseInt(x, 10));
  const total = sumDigits(d) + sumDigits(m) + sumDigits(y);
  return reduceToSingle(total, true);
}

export function calcNameNumber(fullName) {
  if (!fullName) return null;
  const letters = fullName.toLowerCase().replace(/[^a-z]/g, "");
  if (!letters.length) return null;
  const total = letters.split("").reduce((acc, ch) => acc + (CHALDEAN[ch] || 0), 0);
  return reduceToSingle(total, true);
}

// Corrected Western tropical zodiac cusps.
// cuts[i] = last day of previous sign for month (i+1).
// signs aligned so that day <= cuts[m-1] => signs[m-1], else signs[m].
export function calcZodiac(dob) {
  if (!dob) return null;
  const [, m, d] = dob.split("-").map((x) => parseInt(x, 10));
  const cuts = [19, 18, 20, 19, 20, 20, 22, 22, 22, 22, 21, 21];
  const signs = [
    "Capricorn", "Aquarius", "Pisces", "Aries", "Taurus", "Gemini",
    "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn",
  ];
  if (!m || !d) return null;
  return d <= cuts[m - 1] ? signs[m - 1] : signs[m];
}

export function getCurrentYear() {
  return new Date().getFullYear();
}

export function getNextYear() {
  return new Date().getFullYear() + 1;
}

// Personal Year auto-detects current year if not provided. Master numbers NOT preserved.
export function calcPersonalYear(dob, year) {
  if (!dob) return null;
  const yr = year || getCurrentYear();
  const [, m, d] = dob.split("-").map((x) => parseInt(x, 10));
  const total = sumDigits(d) + sumDigits(m) + sumDigits(yr);
  return reduceToSingle(total, false);
}

export const LUCKY_COLOR = {
  1: "Golden Yellow and Orange", 2: "White Silver and Cream",
  3: "Yellow and Violet", 4: "Electric Blue and Grey",
  5: "Green and Silver", 6: "Pink White and Blue",
  7: "Light Yellow and White", 8: "Dark Blue and Black",
  9: "Red and Pink", 11: "Silver and Pale Blue",
  22: "Dark Blue and Earth Tones", 33: "Violet and Gold",
};

export const LUCKY_DAY = {
  1: "Sunday", 2: "Monday", 3: "Thursday", 4: "Saturday",
  5: "Wednesday", 6: "Friday", 7: "Monday", 8: "Saturday",
  9: "Tuesday", 11: "Monday", 22: "Saturday", 33: "Thursday",
};

export const LUCKY_NUMBERS = {
  1: [1, 10, 19], 2: [2, 11, 20], 3: [3, 12, 21],
  4: [4, 13, 22], 5: [5, 14, 23], 6: [6, 15, 24],
  7: [7, 16, 25], 8: [8, 17, 26], 9: [9, 18, 27],
  11: [11, 2, 29], 22: [22, 4, 13], 33: [33, 6, 15],
};

export const RULING_PLANET = {
  1: "Sun", 2: "Moon", 3: "Jupiter", 4: "Rahu", 5: "Mercury",
  6: "Venus", 7: "Ketu", 8: "Saturn", 9: "Mars",
  11: "Moon", 22: "Saturn", 33: "Jupiter",
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function birthMonthName(dob) {
  if (!dob) return "";
  const [, m] = dob.split("-").map((x) => parseInt(x, 10));
  return MONTHS[m - 1] || "";
}

export function fullNumerology(fullName, dob) {
  const lifePath = calcLifePath(dob);
  const nameNumber = calcNameNumber(fullName);
  const zodiac = calcZodiac(dob);
  const personalYear = calcPersonalYear(dob, getCurrentYear());
  const personalYearNext = calcPersonalYear(dob, getNextYear());
  return {
    lifePath,
    nameNumber,
    zodiac,
    personalYear,
    personalYearNext,
    luckyColor: LUCKY_COLOR[lifePath] || "",
    luckyDay: LUCKY_DAY[lifePath] || "",
    luckyNumbers: LUCKY_NUMBERS[lifePath] || [],
    rulingPlanet: RULING_PLANET[lifePath] || "",
    birthMonth: birthMonthName(dob),
  };
}
